import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Alert from '@mui/material/Alert'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Link from '@mui/material/Link'
import { router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface ClaudeAuthorization {
  id: number
  status: string
  oauth_state: string | null
}

interface Props {
  authorization: ClaudeAuthorization
  authorize_url: string
}

export default function New({ authorization, authorize_url }: Props) {
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!code.trim() || !authorization.oauth_state) return
    setSubmitting(true)
    router.post('/manage/claude_authorizations', {
      code: code.trim(),
      state: authorization.oauth_state,
    })
  }

  return (
    <SidebarLayout section="manage" title="New Claude Authorization">
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 700 }}>
        <Stepper activeStep={0} sx={{ mb: 4 }}>
          <Step completed={false}>
            <StepLabel>Authorize with Claude</StepLabel>
          </Step>
          <Step>
            <StepLabel>Paste authorization code</StepLabel>
          </Step>
          <Step>
            <StepLabel>Assign to agent</StepLabel>
          </Step>
        </Stepper>

        <Alert severity="info" sx={{ mb: 3 }}>
          This will redirect you to Anthropic to authorize Claude Code access. After authorization, you will receive a code to paste back here.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Step 1: Open the authorization link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click the link below to open the Anthropic OAuth page. Sign in and authorize access.
          </Typography>
          <Link href={authorize_url} target="_blank" rel="noopener noreferrer">
            Open Anthropic Authorization Page
          </Link>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Step 2: Paste the authorization code
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            After authorizing, you will see a code on the success page. Copy and paste it below.
          </Typography>
          <TextField
            fullWidth
            label="Authorization code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste the authorization code here"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!code.trim() || submitting}
            >
              {submitting ? 'Completing...' : 'Complete Authorization'}
            </Button>
            <Button
              variant="outlined"
              href="/manage/claude_authorizations"
              component="a"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
